import { useEffect, useMemo, useState } from 'react';
import { PageHeader, StatTile } from '../components/Scaffold';
import api from '../api/client';

/**
 * Kiosk customer feedback, across every café.
 *
 * A customer who presses Log out on the kiosk gets a feedback slide: a topic
 * from a fixed list and up to 200 characters of text (ClutcHH-1
 * features/feedback). What they send lands in the global DB
 * (backend/app/db/models_global.py: CustomerFeedback) with the café, the
 * customer's name and phone, and the PC, all as they were at the time.
 *
 * Endpoint: GET /api/internal/customer-feedback?cafe_id&topic&since&until.
 * Café, topic and dates filter on the server; the search box filters what
 * came back.
 */

// Same keys as the kiosk (ClutcHH-1/src/features/feedback/topics.js) and the
// backend (FEEDBACK_TOPICS). A key missing here still shows, as itself.
const TOPIC_LABELS = {
  games_apps: 'Games & apps',
  pc_performance: 'PC speed & performance',
  prices_offers: 'Prices, packs & offers',
  shop_food: 'Shop & food orders',
  login_account: 'Login & account',
  kiosk_app: 'Kiosk app (screens, buttons)',
  hardware: 'PC or hardware problem',
  staff_service: 'Café staff & service',
  suggestion: 'A suggestion for NoLag',
  other: 'Something else',
};
const topicLabel = (key) => TOPIC_LABELS[key] || key;

function parseUtc(iso) {
  return new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
}

// A <input type="date"> value ("2026-10-15") as the viewer's local midnight.
function localMidnight(day) {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export default function CustomerFeedback() {
  const [cafes, setCafes] = useState([]);
  const [cafeId, setCafeId] = useState('');
  const [topic, setTopic] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/internal/cafes')
      .then((res) => setCafes(res.data || []))
      .catch((err) => console.error('Failed to fetch cafés:', err));
  }, []);

  // The server-side filters, as one string so the fetch below re-runs only
  // when one of them actually changes.
  const query = useMemo(() => {
    const params = {};
    if (cafeId) params.cafe_id = cafeId;
    if (topic) params.topic = topic;
    if (from) params.since = localMidnight(from).toISOString();
    if (to) {
      // Up to the END of the "to" day: midnight of the day after.
      const end = localMidnight(to);
      end.setDate(end.getDate() + 1);
      params.until = end.toISOString();
    }
    return JSON.stringify(params);
  }, [cafeId, topic, from, to]);

  // Tagged with the query it answers: loading is "the last answer is for a
  // different query", and a slow answer to an older query can't overwrite a
  // newer one. `at` is when it arrived, for "Last 7 days".
  const [result, setResult] = useState({ query: null, rows: [], error: null, at: 0 });
  useEffect(() => {
    let current = true;
    api.get('/internal/customer-feedback', { params: JSON.parse(query) })
      .then((res) => {
        if (current) setResult({ query, rows: res.data || [], error: null, at: Date.now() });
      })
      .catch((err) => {
        console.error('Failed to fetch customer feedback:', err);
        if (current) {
          setResult({
            query,
            rows: [],
            error: err.response?.data?.detail || 'Failed to load customer feedback',
            at: Date.now(),
          });
        }
      });
    return () => { current = false; };
  }, [query]);
  const isLoading = result.query !== query;
  const { rows, error } = result;

  const cafeName = useMemo(() => {
    const map = new Map(cafes.map((c) => [c.id, c.name]));
    return (row) => row.cafe_name || map.get(row.cafe_id) || `Café ${row.cafe_id}`;
  }, [cafes]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [
      r.customer_name, r.customer_phone, r.pc_name, r.message, cafeName(r), topicLabel(r.topic),
    ].some((v) => v && String(v).toLowerCase().includes(q)));
  }, [rows, search, cafeName]);

  const stats = useMemo(() => {
    const weekAgo = result.at - 7 * 24 * 60 * 60 * 1000;
    const counts = {};
    let lastWeek = 0;
    visible.forEach((r) => {
      counts[r.topic] = (counts[r.topic] || 0) + 1;
      if (parseUtc(r.created_at).getTime() >= weekAgo) lastWeek += 1;
    });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return { total: visible.length, lastWeek, top };
  }, [visible, result.at]);

  const hasFilters = cafeId || topic || from || to || search;
  const clearFilters = () => { setCafeId(''); setTopic(''); setFrom(''); setTo(''); setSearch(''); };

  return (
    <div className="customer-feedback-page">
      <style>{`
        .customer-feedback-page .fb-filters { display: flex; gap: 8px; margin: 16px 0; flex-wrap: wrap; align-items: center; }
        .customer-feedback-page .fb-filters .field__select,
        .customer-feedback-page .fb-filters .field__input { width: auto; }
        .customer-feedback-page .fb-search { margin-left: auto; width: 260px; max-width: 100%; }
        .customer-feedback-page .fb-message { white-space: pre-wrap; word-break: break-word; min-width: 240px; }
        .customer-feedback-page .fb-range-sep { color: var(--text-tertiary); }
      `}</style>
      <PageHeader
        title="Customer Feedback"
        subtitle="What kiosk customers said when they logged out"
      />

      <div className="stat-grid">
        <StatTile label="Feedback" value={stats.total} hint={hasFilters ? 'Matching the filters' : 'All cafés'} />
        <StatTile label="Last 7 days" value={stats.lastWeek} />
        <StatTile
          label="Most mentioned"
          value={stats.top ? topicLabel(stats.top[0]) : '—'}
          hint={stats.top ? `${stats.top[1]} of ${stats.total}` : null}
        />
      </div>

      <div className="fb-filters">
        <select className="field__select" value={cafeId} onChange={(e) => setCafeId(e.target.value)} aria-label="Café">
          <option value="">All cafés</option>
          {[...cafes].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select className="field__select" value={topic} onChange={(e) => setTopic(e.target.value)} aria-label="Topic">
          <option value="">All topics</option>
          {Object.entries(TOPIC_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <input
          type="date"
          className="field__input"
          value={from}
          max={to || undefined}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="From date"
        />
        <span className="fb-range-sep">–</span>
        <input
          type="date"
          className="field__input"
          value={to}
          min={from || undefined}
          onChange={(e) => setTo(e.target.value)}
          aria-label="To date"
        />
        {hasFilters && (
          <button type="button" className="btn btn--sm btn--secondary" onClick={clearFilters}>Clear</button>
        )}
        <input
          type="search"
          className="field__input fb-search"
          placeholder="Search name, phone, PC, feedback…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="card table-card">
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Café</th>
                <th>Customer</th>
                <th>Phone</th>
                <th>PC</th>
                <th>Topic</th>
                <th>Feedback</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} className="text-tertiary">Loading…</td></tr>
              ) : error ? (
                <tr><td colSpan={7} className="text-tertiary">{error}</td></tr>
              ) : visible.length === 0 ? (
                <tr><td colSpan={7} className="text-tertiary">{hasFilters ? 'No feedback matches the filters.' : 'No feedback yet.'}</td></tr>
              ) : visible.map((r) => (
                <tr key={r.id}>
                  <td className="text-tertiary" style={{ whiteSpace: 'nowrap' }}>{parseUtc(r.created_at).toLocaleString()}</td>
                  <td>{cafeName(r)}</td>
                  <td>{r.customer_name || <span className="text-tertiary">—</span>}</td>
                  <td className="font-mono" style={{ whiteSpace: 'nowrap' }}>{r.customer_phone || <span className="text-tertiary">—</span>}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.pc_name || <span className="text-tertiary">—</span>}</td>
                  <td style={{ whiteSpace: 'nowrap' }}><span className="badge badge--info">{topicLabel(r.topic)}</span></td>
                  <td className="text-secondary fb-message">{r.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
